import { useContext, useMemo } from "react";
import { CircularProgress, IconButton } from "@mui/material";
import { DisplayInOptionsEnum } from "constants/constants";
import CloseIcon from "@mui/icons-material/Close";
import ReportCard from "components/ReportCard";
import ReportDowntimeBarChart from "components/ReportDowntimeBarChart";
import { useStyles } from "./SummaryReportPopover.styles";
import { SummaryReportDashboardContext } from "contexts/SummaryReportDashboardContext/SummaryReportDashboardContext";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { IMillReportCardProps } from "pages/Corporate/Locations/MillDashboards/MillDashboardInterfaces";
import { MillDashboardContext } from "contexts/MillDashboardsContext/MillDashboardsContext";
import { handleNameTranslation } from "utilities/localesUtilities";
import { useTranslation } from "react-i18next";

const SummaryReportPopover = ({ countryCode }: IMillReportCardProps) => {
    const { i18n } = useTranslation();

    const {
        handleSetSelectedDowntimeBar,
        handleSetSelectedMachineCenterByCode,
        filters,
        selectedDowntimeBarTitle,
        selectedFailureCode,
        selectedFailureType,
        selectedMachineCenterByCode,
        totalDowntimeSeconds,
    } = useContext(SummaryReportDashboardContext);

    const {
        displayUnit: displayIn,
        reportRangeEndDate: endDate,
        reportRangeStartDate: startDate,
    } = useContext(MillDashboardContext);

    const queryFilters = useMemo(
        () => ({
            ...filters,
            startDate,
            endDate,
            displayIn,
        }),
        [filters, startDate, endDate, displayIn],
    );

    const classes = useStyles();

    const { downtimeSummaryPerFunctionalLocation, loading } =
        MillsEntity.GetTotalDowntimeSummaryByCodePerFunctionalLocationMillDashboard(
            queryFilters,
            Number(selectedMachineCenterByCode),
            Number(selectedFailureCode?.id),
        );

    const graphData = useMemo(
        () =>
            downtimeSummaryPerFunctionalLocation
                .map((downtimeSummary) => {
                    const secondsToMinutes =
                        Math.round(downtimeSummary.summary.aggregate.sum.seconds / 60) < 1
                            ? 1
                            : Math.round(downtimeSummary.summary.aggregate.sum.seconds / 60);

                    const barValue =
                        displayIn === DisplayInOptionsEnum.MINUTES
                            ? secondsToMinutes
                            : displayIn === DisplayInOptionsEnum.PERCENT
                            ? totalDowntimeSeconds
                                ? (100 * downtimeSummary.summary.aggregate.sum.seconds) / totalDowntimeSeconds
                                : 0
                            : downtimeSummary.summary.aggregate.sum.occurrences;
                    return {
                        functionalLocationId: downtimeSummary.functional_location_id,
                        functionalLocation: handleNameTranslation(
                            i18n.language,
                            downtimeSummary.translation.translation_names,
                        ),
                        [`${selectedFailureCode?.name}`]: barValue,
                        [`${selectedFailureCode?.name}Color`]: selectedFailureType?.color_code || "",
                        totalDowntimeByMill: barValue,
                        countryCode: countryCode as string,
                    };
                })
                .sort((a, b) => Number(a.totalDowntimeByMill) - Number(b.totalDowntimeByMill))
                .slice(-5),
        [
            downtimeSummaryPerFunctionalLocation,
            displayIn,
            totalDowntimeSeconds,
            selectedFailureCode?.name,
            selectedFailureType?.color_code,
            countryCode,
            i18n.language,
        ],
    );

    return (
        <ReportCard
            showNoData={graphData?.length === 0}
            title={selectedDowntimeBarTitle || ""}
            className={classes.mainCardReportWrapper}
            headerActions={
                <IconButton
                    size="small"
                    aria-label="delete"
                    onClick={() => {
                        handleSetSelectedDowntimeBar(null);
                        handleSetSelectedMachineCenterByCode(undefined);
                    }}
                    className={classes.closeButton}
                >
                    <CloseIcon className={classes.closeButtonIcon} />
                </IconButton>
            }
        >
            {loading ? (
                <div className={classes.noData}>
                    <CircularProgress size={24} color="info" />
                </div>
            ) : (
                <div className={classes.graphWrapper}>
                    <ReportDowntimeBarChart
                        data={graphData || []}
                        noPercent={displayIn !== DisplayInOptionsEnum.PERCENT}
                        index="functionalLocation"
                        noTrend
                    />
                </div>
            )}
        </ReportCard>
    );
};

export default SummaryReportPopover;
